//
// # PropertiesController
//
// Owns the Properties Panel's selection state and the logic that describes and
// edits the selected entity. Extracted from Workspace to keep that class
// focused: Workspace delegates its public Properties-Panel methods here and
// reads `selection` / `selectionVersion` when it builds its snapshot.
//
// The controller reaches back into the workspace only through the narrow
// `PropertiesHost` interface (active editor, active design, dirty-marking,
// instance create/update, content-version bump, snapshot publish), so it stays
// decoupled from the rest of Workspace.
//

import { logger, Result } from "CoreUtils";
import { Editor, type SelectionInfo, type FieldEdits } from "EditorCore";
import { Ident } from "GenAlphaModel";
import { schema } from "GenAlphaSchema";
import type {
  SymbolInfo,
  CreateInstanceResult,
  InstanceDetails,
} from "ServerInterface";
import { AlphaClient } from "./client";
import { createRequestContext } from "./contextHelpers";
import { extractErrorMessage } from "./errorUtil";
import {
  SymbolInfoArrayValidator,
  InstanceDetailsValidator,
} from "./serverValidators";
import {
  selectionEq,
  describeSchInstance,
  describeSchPin,
  describeLayoutInstance,
  describeLayoutPin,
  toRotation,
} from "./selectionDescriptor";
import type {
  WorkspaceSelection,
  EntityDescriptor,
  EntityEdits,
} from "./types";

// The narrow surface of Workspace the controller depends on. Implemented by an
// adapter the Workspace builds in its constructor, so the controller never
// touches Workspace internals directly.
export interface PropertiesHost {
  readonly client: AlphaClient;
  getUserId(): string | undefined;
  getActiveEditor(): Editor | null;
  getActiveIdent(): Ident | null;
  markDirty(ident: Ident): void;
  createInstance(params: {
    designIdent: schema.Ident;
    symbolName: string;
    pdkId?: string;
    params?: Record<string, unknown>;
    position?: { x: number; y: number };
    name?: string;
  }): Promise<Result<CreateInstanceResult>>;
  updateInstance(params: {
    designIdent: schema.Ident;
    instanceId: string;
    name?: string;
    params?: Record<string, unknown>;
    position?: { x: number; y: number };
  }): Promise<Result<unknown>>;
  // Bump the global content version (a structural change → other content panels
  // re-fetch). Distinct from selectionVersion, which only the panel reads.
  bumpContentVersion(): void;
  // Re-publish the workspace snapshot to its listeners.
  publishSnapshot(): void;
}

export class PropertiesController {
  // The current editor selection (for the Properties Panel), or null.
  private _selection: WorkspaceSelection | null = null;
  // Bumped whenever the panel needs to re-derive its descriptor (selection
  // change or a local edit to the selected entity).
  private _selectionVersion = 0;

  constructor(private readonly host: PropertiesHost) {}

  get selection(): WorkspaceSelection | null {
    return this._selection;
  }

  get selectionVersion(): number {
    return this._selectionVersion;
  }

  /**
   * Record a selection change reported by an editor (via WorkspacePlatform).
   * Only the active design's selection drives the panel; a notification from a
   * background editor is ignored. Bumps selectionVersion when the selected
   * entity changes so the panel re-seeds its draft.
   *
   * @param designIdent - The design that reported the selection. Ignored unless
   *   it matches the active editor's design.
   * @param info - The new selection, or `null` to clear it.
   */
  setSelection(designIdent: Ident, info: SelectionInfo | null): void {
    // Ignore notifications from non-active editors (stale background tabs).
    const activeIdent = this.host.getActiveIdent();
    if (
      !activeIdent ||
      Ident.stringify(activeIdent) !== Ident.stringify(designIdent)
    ) {
      return;
    }

    const next: WorkspaceSelection | null =
      info === null ? null : { designIdent, info };

    // Skip the snapshot churn + version bump if nothing actually changed.
    if (selectionEq(this._selection, next)) return;

    this._selection = next;
    this._selectionVersion++;
    this.host.publishSnapshot();
  }

  // Clear any stale selection from the previously-active design so a background
  // editor's selection never leaks into the Properties Panel. Does NOT publish
  // a snapshot — the active-design change that triggers this publishes its own.
  clearSelection(): void {
    if (this._selection !== null) {
      this._selection = null;
      this._selectionVersion++;
    }
  }

  // Called after a local EditorCore edit (e.g. a canvas drag-move, rotate, or a
  // panel Apply) so the Properties panel re-derives the selected entity's
  // descriptor — local edits go through notifyUIChange, which (unlike a server
  // design-load refresh) does not bump contentVersion. We bump selectionVersion
  // (read only by the Properties panel) rather than contentVersion to avoid
  // triggering network re-fetches in the other content panels (constraints,
  // LVS, DRC, …). The panel's descriptor is content-stabilized, so an edit to a
  // different entity is a no-op for an unchanged selection.
  notifyLocalEdit(): void {
    this._selectionVersion++;
    this.host.publishSnapshot();
  }

  /**
   * Build a discriminated-union view model of the current selection from the
   * live EditorCore entity. Async params (params / paramSpec) are NOT fetched
   * here; the panel fetches them via get-instance and the descriptor reports
   * `paramsLoading`. Reads are done defensively (no type assertions) to keep
   * the workspace decoupled from EditorCore-internal entity types.
   *
   * @returns The selected entity's descriptor, `{ kind: "multi" }` for a
   *   multi-selection, `{ kind: "unsupported" }` for an entity kind without an
   *   editable view, or `null` when nothing is selected (or no active editor).
   */
  describeSelection(): EntityDescriptor | null {
    const selection = this._selection;
    if (selection === null) return null;

    const editor = this.host.getActiveEditor();
    if (!editor) return null;

    const { info, designIdent } = selection;

    if (info.count > 1) {
      return { kind: "multi", count: info.count };
    }

    // The live, most-recently-selected entity.
    const entity: unknown = editor.content.selected;
    if (entity === null || entity === undefined) return null;

    switch (info.entityKind) {
      case "Instance":
        return describeSchInstance(entity, designIdent);
      case "SchPin":
        return describeSchPin(entity, designIdent);
      case "LayoutInstance":
        return describeLayoutInstance(entity, designIdent, editor.showOutlines);
      case "LayoutPin":
        return describeLayoutPin(entity, designIdent);
      default:
        return { kind: "unsupported", entityKind: info.entityKind };
    }
  }

  /**
   * Apply buffered edits from the Properties Panel. Batchable field edits are
   * routed through the local change path (`content.applyFieldEdits`, which logs
   * one Combo) so they form a single atomic undo step and a single server
   * "changes" request. Generator-backed layout-instance param edits instead go
   * through the update-instance endpoint (see `applyLayoutInstanceViaUpdate`).
   *
   * Returns a Promise so later phases (type-change delete+create, generator
   * param regen) can perform async server calls without changing the panel's
   * call site; the batchable-field path itself is synchronous.
   *
   * @param edits - The dirty fields to apply, tagged with the design + entity.
   * @returns `Ok` on success, or `Err({ why })` (no-active-editor /
   *   selection-not-active / selection-has-no-id / an apply failure) that the
   *   panel surfaces in its footer.
   */
  applyEntityEdits(edits: EntityEdits): Promise<Result<void, { why: string }>> {
    const editor = this.host.getActiveEditor();
    if (!editor) {
      return Promise.resolve(Result.Err({ why: "no-active-editor" }));
    }

    // Confirm the active editor still owns the selection being edited.
    const activeIdent = this.host.getActiveIdent();
    if (
      !activeIdent ||
      Ident.stringify(activeIdent) !== Ident.stringify(edits.designIdent)
    ) {
      return Promise.resolve(Result.Err({ why: "selection-not-active" }));
    }
    const entityId = edits.info.id;
    if (entityId === undefined) {
      return Promise.resolve(Result.Err({ why: "selection-has-no-id" }));
    }

    // Documented seam: generator-backed LAYOUT instance params require
    // server-side regeneration via update-instance (they bypass the local undo
    // log). Route the whole edit through updateInstance in that case.
    if (
      edits.info.entityKind === "LayoutInstance" &&
      edits.params !== undefined
    ) {
      return this.applyLayoutInstanceViaUpdate(edits, entityId);
    }

    const fieldEdits: FieldEdits = {
      id: entityId,
      entityKind: edits.info.entityKind,
      name: edits.name,
      position: edits.position,
      rotation:
        edits.rotation === undefined ? undefined : toRotation(edits.rotation),
      reflected: edits.reflected,
      direction: edits.direction,
      layer: edits.layer,
      pinType: edits.pinType,
      params: edits.params,
    };

    const result = editor.content.applyFieldEdits(fieldEdits);
    if (result.err) return Promise.resolve(result);

    // The eager-apply already mutated the live entity, so commit the edit to
    // re-seed the panel's baseline (the descriptor now reflects the new values).
    this.commitEntityEdit(edits.designIdent);
    return Promise.resolve(Result.Ok(undefined));
  }

  // Seam: apply a layout-instance edit that includes generator params through
  // the update-instance endpoint (server regenerates the instance). name /
  // position are folded into the same request. This is outside the local undo
  // Combo by design (the endpoint bypasses the local change log).
  private async applyLayoutInstanceViaUpdate(
    edits: EntityEdits,
    entityId: number
  ): Promise<Result<void, { why: string }>> {
    const result = await this.host.updateInstance({
      designIdent: edits.designIdent,
      instanceId: String(entityId),
      name: edits.name,
      params: edits.params,
      position: edits.position,
    });
    if (result.err) {
      return Result.Err({ why: extractErrorMessage(result.val) });
    }
    this.commitEntityEdit(edits.designIdent);
    return Result.Ok(undefined);
  }

  // Commit a Properties Panel edit that has already mutated the live entity:
  // mark the design dirty (so it is saved) and bump selectionVersion so the
  // panel re-derives the descriptor and re-seeds its baseline draft. We bump
  // selectionVersion (not contentVersion) to avoid triggering network
  // re-fetches in the other content panels.
  private commitEntityEdit(designIdent: Ident): void {
    this.host.markDirty(designIdent);
    this._selectionVersion++;
    this.host.publishSnapshot();
  }

  // Toggle the editor-global "show outlines" flag (surfaced in the layout
  // Properties Panel as a convenience). Because the flag is editor-wide this
  // affects all visible outlines, not just the selected instance. On first
  // enable for a layout, seeds the current cell's outline from its bbox via
  // initOutline(). Bumps selectionVersion so the panel reflects the new flag.
  setShowOutlines(show: boolean): void {
    const editor = this.host.getActiveEditor();
    if (!editor) return;
    editor.showOutlines = show;
    const content = editor.content;
    if (show && content.contentKind === "layout") {
      content.initOutline();
    }
    editor.requestRedraw();
    this._selectionVersion++;
    this.host.publishSnapshot();
  }

  // Fetch full instance details (params + paramSpec) for the Properties Panel.
  // Mirrors the dialog's get-instance fetch, but returns the validated details
  // (or null) rather than mutating dialog state. Used by usePropertiesDescriptor
  // to populate instance/layoutInstance descriptors asynchronously.
  async getInstanceDetails(
    designIdent: Ident,
    instanceId: number
  ): Promise<InstanceDetails | null> {
    const ctx = createRequestContext(this.host.getUserId());
    const result = await this.host.client.request(ctx, {
      kind: "get-instance",
      data: { designIdent, instanceId: String(instanceId) },
    });
    const validated = result.andThen(
      InstanceDetailsValidator.validator.validate
    );
    if (validated.err) {
      logger.warn(
        `[Workspace] getInstanceDetails failed: ${extractErrorMessage(
          validated.val
        )}`
      );
      return null;
    }
    return validated.val;
  }

  // Fetch the same-class symbol candidates for the Properties Panel's type
  // picker. Resolves the design's pdkId then lists available symbols (the
  // server already returns the candidates appropriate to the design's content
  // kind). Returns [] on failure.
  async getAvailableSymbolsForDesign(
    designIdent: Ident
  ): Promise<SymbolInfo[]> {
    const ctx = createRequestContext(this.host.getUserId());
    const contentResult = await this.host.client.request(ctx, {
      kind: "open",
      data: { kind: "ident", ident: designIdent },
    });
    let pdkId: string | undefined;
    if (!contentResult.err) {
      const val = contentResult.val;
      if (typeof val === "object" && val !== null) {
        const candidate: unknown = Reflect.get(val, "pdkId");
        if (typeof candidate === "string") pdkId = candidate;
      }
    }
    const result = await this.host.client.request(ctx, {
      kind: "list-available-symbols",
      data: { pdkId, ident: designIdent },
    });
    const validated = result.andThen(
      SymbolInfoArrayValidator.validator.validate
    );
    return validated.err ? [] : validated.val;
  }

  // Change a schematic instance's type (target symbol/generator) via a
  // client-side delete + create (no new server endpoints), recorded as a single
  // undoable Combo so the whole operation reverses in one step.
  //
  // The deletion is mirrored locally as a record-only Remove (NOT a refresh): a
  // refresh swaps the Content object and discards the entire undo stack (see
  // Editor.loadDrawnContent's refresh FIXME), which is what used to make this
  // operation non-undoable. The create records an Add, the orientation re-apply
  // (create-instance only takes position) records an Update, and all three are
  // folded into one Combo via combineRecentChanges — so undoing the type-change
  // removes the new instance AND restores the original. On create-failure the
  // original instance (held live below) is re-added locally and on the server,
  // with no data-loss window.
  async changeInstanceType(args: {
    designIdent: Ident;
    oldInstanceId: number;
    newSymbolName: string;
    name: string;
    position: { x: number; y: number };
    rotation: schema.Rotation;
    reflected: boolean;
    params?: Record<string, unknown>;
  }): Promise<Result<void, { why: string }>> {
    const editor = this.host.getActiveEditor();
    if (!editor) return Result.Err({ why: "no-active-editor" });
    const schematic = editor.getSchematicContent();
    if (!schematic) return Result.Err({ why: "type-change-only-schematic" });

    // Hold the live original instance: it is removed below, used to restore the
    // design if the create fails, and its recorded Remove is what lets undo
    // bring the original type back.
    const oldInstance = schematic.findInstance(args.oldInstanceId);
    if (!oldInstance) return Result.Err({ why: "instance-not-found" });

    const ctx = createRequestContext(this.host.getUserId());

    // 1. Delete the old instance on the server (delete first so the reused name
    // is free), then mirror the deletion locally as a record-only Remove.
    const deleteResult = await this.host.client.request(ctx, {
      kind: "delete-instance",
      data: {
        designIdent: args.designIdent,
        instanceId: String(args.oldInstanceId),
      },
    });
    if (deleteResult.err) {
      return Result.Err({ why: extractErrorMessage(deleteResult.val) });
    }
    schematic.removeEntityRecordOnly(oldInstance);

    // 2. Create the new instance of the chosen type at the same name/position
    // (records an Add locally).
    const createResult = await this.host.createInstance({
      designIdent: args.designIdent,
      symbolName: args.newSymbolName,
      name: args.name,
      position: args.position,
      params: args.params,
    });
    if (createResult.err) {
      // Create failed after delete — roll back by re-adding the original
      // instance (locally and on the server) and discarding the speculative
      // Remove, so no data is lost and the undo stack is left clean.
      logger.error(
        `[Workspace] changeInstanceType: create failed after delete, restoring original: ${extractErrorMessage(
          createResult.val
        )}`
      );
      schematic.rollbackRecordOnlyRemoval(oldInstance);
      this._selection = {
        designIdent: args.designIdent,
        info: { entityKind: "Instance", id: args.oldInstanceId, count: 1 },
      };
      this.host.markDirty(args.designIdent);
      this._selectionVersion++;
      this.host.bumpContentVersion();
      this.host.publishSnapshot();
      return Result.Err({
        why: `${extractErrorMessage(
          createResult.val
        )} (original instance restored)`,
      });
    }

    // 3. Re-apply orientation on the new instance (create-instance drops xform),
    // logged as an Update and synced to the server.
    const newId = parseInt(createResult.val.instanceId, 10);
    let changeCount = 2; // record-only Remove + create Add
    if (!Number.isNaN(newId)) {
      const orient = schematic.applyFieldEdits({
        id: newId,
        entityKind: "Instance",
        rotation: args.rotation,
        reflected: args.reflected,
      });
      if (orient.ok) changeCount = 3;
    }

    // Fold the Remove + Add (+ orientation) into a single atomic undo step.
    // combineRecentChanges is total: it clamps to the changes actually present
    // and discards any undone tail, so it can't fail the (already-applied) op.
    schematic.combineRecentChanges(changeCount);

    // The new instance is already selected by host.createInstance (the local
    // create path selects it and clears the now-deleted original), so the panel
    // tracks it via the normal setSelection channel — no manual select here.

    this.host.markDirty(args.designIdent);
    this._selectionVersion++;
    this.host.bumpContentVersion();
    this.host.publishSnapshot();
    return Result.Ok(undefined);
  }
}
